  // Two page-local behaviours: the filter overlay toggle and the grid switcher.
  // Both are collection-only, so they live here rather than in the global bundle.
  //
  // Both are delegated from document rather than bound to their buttons. The
  // toolbar sits inside a [data-facet-target] region, and <facet-form> replaces
  // that region's innerHTML after every filter change — anything bound directly
  // to a button in there is dead the moment a shopper ticks their first filter.
  // <drawer-element> wires its own openers once at connect time, which is why the
  // FILTERS button deliberately does not use data-drawer-open.
  (() => {
    const drawer = document.getElementById('FilterDrawer');

    if (drawer) {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-filters-toggle]');
        if (!btn) return;
        e.preventDefault();
        if (drawer.dataset.open === 'true') drawer.close();
        else drawer.open(btn);
      });

      // Keep every toggle's aria-expanded in step however the panel was dismissed —
      // overlay click, Escape, or the Apply button.
      new MutationObserver(() => {
        const open = drawer.dataset.open === 'true';
        document.querySelectorAll('[data-filters-toggle]').forEach((b) => {
          b.setAttribute('aria-expanded', String(open));
        });
      }).observe(drawer, { attributes: true, attributeFilter: ['data-open'] });
    }

    const root = document.querySelector('.collection[data-cols]');
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-grid-toggle]') || !root) return;
      const next = root.dataset.cols === '4' ? '3' : '4';
      root.dataset.cols = next;
      root.style.setProperty('--cols', next);
    });

    // Shop the Look tiles sit inside the swapped grid region, so their opener is
    // delegated too rather than wired by <drawer-element> at connect time.
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-look-open]');
      if (!opener) return;
      e.preventDefault();
      const panel = document.getElementById(opener.dataset.lookOpen);
      if (panel && typeof panel.open === 'function') panel.open(opener);
    });
  })();

  // <load-more-products> — the grid's own loader.
  //
  // theme.js ships a generic <load-more> that only carries the next page's URL
  // forward. That is fine for an unlabelled "next" control, but this button
  // names a quantity, and a quantity has to keep telling the truth after every
  // append. So the collection gets its own element rather than a count bolted
  // onto the generic one.
  //
  // It never counts DOM nodes: the grid also holds promo tiles, which would
  // inflate any such count. Instead every page Liquid renders carries its own
  // totals (data-total / data-loaded) and its own already-localised label, and
  // this adopts the page it just appended. Server truth in, server truth out —
  // no number is ever computed twice, and the string stays in the locale file.
  (() => {
    if (customElements.get('load-more-products')) return;

    customElements.define('load-more-products', class extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('button');
        this.target = document.querySelector(this.dataset.target);
        if (!this.btn || !this.target) return;

        // The "24 / 50 products viewed" line is a sibling, not a child.
        this.progress = this.parentElement?.querySelector('[data-load-progress]') || null;

        this.btn.addEventListener('click', () => this.load());

        if (this.dataset.mode === 'infinite') {
          this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) this.load();
          }, { rootMargin: '600px' });
          this.observer.observe(this);
          this.btn.hidden = true;
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async load() {
        const next = this.dataset.nextUrl;
        if (!next || this.loading) return;
        this.loading = true;
        this.btn.classList.add('loading');

        try {
          const res = await fetch(next);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const items = doc.querySelector(this.dataset.target)?.children;
          if (items) this.target.append(...items);

          this.sync(doc.querySelector('load-more-products'), doc);
        } catch {
          window.location.href = next;
        } finally {
          this.loading = false;
          this.btn.classList.remove('loading');
        }
      }

      sync(fresh, doc) {
        // No element on the next page means that page was the last one.
        if (!fresh?.dataset.nextUrl) { this.finish(); return; }

        const total = Number(fresh.dataset.total);
        const loaded = Number(fresh.dataset.loaded);
        const counted = Number.isFinite(total) && Number.isFinite(loaded) && total > 0;
        const remaining = counted ? Math.max(0, total - loaded) : null;

        // Nothing left to offer — drop the control rather than say "Load 0 more".
        if (remaining === 0) { this.finish(); return; }

        this.dataset.nextUrl = fresh.dataset.nextUrl;

        if (counted) {
          this.dataset.total = String(total);
          this.dataset.loaded = String(loaded);
          this.dataset.remaining = String(remaining);
        }

        const label = fresh.querySelector('button')?.textContent.trim();
        if (label) this.btn.textContent = label;

        const freshProgress = doc?.querySelector('[data-load-progress]');
        if (this.progress && freshProgress) {
          this.progress.textContent = freshProgress.textContent.trim();
        }
      }

      // Liquid renders no "load more" block at all on a last page, so take the
      // whole block with us — leaving the progress line behind would strand it
      // reading "24 / 30" above a grid of 30.
      finish() {
        this.observer?.disconnect();
        (this.closest('.collection__more') || this).remove();
      }
    });
  })();
